import { useState, useEffect, useRef, useCallback } from 'react';

const useScrollProgress = () => {
  const scrollContentRef = useRef(null);
  const progressBarRef = useRef(null);
  const layoutRef = useRef({ top: 0, maxScroll: 0 });
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [backToTopVisible, setBackToTopVisible] = useState(false);

  const cacheLayout = useCallback(() => {
    if (!scrollContentRef.current) return;
    const contentTop = scrollContentRef.current.offsetTop;
    const contentHeight = scrollContentRef.current.offsetHeight;
    const windowHeight = window.innerHeight;
    layoutRef.current = {
      top: contentTop,
      maxScroll: contentHeight - windowHeight,
    };
  }, []);

  useEffect(() => {
    cacheLayout();
    window.addEventListener('resize', cacheLayout, { passive: true });

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const { top, maxScroll } = layoutRef.current;

        // READ phase — all geometry reads first
        const contentTop = scrollContentRef.current
          ? scrollContentRef.current.getBoundingClientRect().top
          : 0;
        const scrollingUp = scrollTop < lastScrollY.current;

        // WRITE phase — all DOM mutations after reads
        if (progressBarRef.current) {
          let percent = 0;
          if (scrollTop > top && maxScroll > 0) {
            percent = Math.min(1, (scrollTop - top) / maxScroll);
          }
          progressBarRef.current.style.width = `${percent * 100}%`;
        }

        setHeaderVisible(scrollingUp && contentTop < 0);
        setBackToTopVisible(contentTop < -200);

        lastScrollY.current = scrollTop;
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', cacheLayout);
    };
  }, [cacheLayout]);

  return { scrollContentRef, progressBarRef, headerVisible, backToTopVisible };
};

export default useScrollProgress;
